import { useAuth } from "@clerk/react"
import { Dices } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import {
  ArtworkDropTarget,
  ArtworkOverlayControls,
} from "../cards/ArtworkControls"
import type { CardEditingCallbacks } from "../cards/CardFace"
import { CardFace } from "../cards/CardFace"
import { CostBadge } from "../cards/CostBadge"
import { PortalTargetProvider } from "../cards/PortalTarget"
import {
  HERO_FORBIDDEN_SKILL_IDS,
  randomizeHeroLevels,
} from "../cards/randomize"
import { useIdbState } from "../hooks/useIdbState"
import { useStoredState } from "../hooks/useStoredState"
import { useI18n } from "../i18n/useI18n"
import { markRecordSyncPending } from "../library/syncState"
import { SKILL_MAP } from "../match/engine/content/skills"
import {
  computeSkillCostBreakdown,
  computeStatCost,
} from "../match/engine/costs"
import { validateHero } from "../match/engine/validator/heroes"
import {
  ARTWORK_ASSET_STORAGE_KEY,
  ARTWORK_ASSET_STORAGE_VERSION,
  ArtworkAssetStoreSchema,
  collectArtworkAssets,
  EMPTY_ARTWORK_ASSET_STORE,
  upsertArtworkAssets,
} from "../media/assets"
import { getArtworkErrorLabel } from "../media/errors"
import { processArtworkFile } from "../media/processArtwork"
import { useArtworkSyncMutation } from "../media/query"
import { requestTurnstileToken } from "../media/turnstile"
import {
  ARTWORK_UPLOAD_STORAGE_KEY,
  ARTWORK_UPLOAD_STORAGE_VERSION,
  ArtworkUploadStoreSchema,
  createArtworkUploadRecord,
  EMPTY_ARTWORK_UPLOAD_STORE,
  updateArtworkUploadStatus,
  upsertArtworkUpload,
} from "../media/uploads"
import {
  createModerationIncident,
  EMPTY_MODERATION_INCIDENT_STORE,
  MODERATION_INCIDENT_STORAGE_KEY,
  MODERATION_INCIDENT_STORAGE_VERSION,
  ModerationIncidentStoreSchema,
} from "../moderation/storage"
import { validateModeratedText } from "../moderation/text"
import type {
  CardArtwork,
  CardColor,
  HeroDefinition,
  ManagedHeroRecord,
  SkillSelection,
} from "../schemas"
import { createHeroId } from "./storage"
import { getHeroValidationIssueLabel } from "./validationMessages"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Props = {
  open: boolean
  onClose: () => void
  editingRecord: ManagedHeroRecord | null
  onSave: (record: ManagedHeroRecord) => void
}

type LevelValues = {
  attack: string
  health: string
  artwork?: CardArtwork
  skills: SkillSelection[]
}

type FormValues = {
  name: string
  primaryColor: CardColor
  secondaryColor: CardColor | ""
  levels: [LevelValues, LevelValues, LevelValues]
}

type ArtworkSyncStatus = "localOnly" | "retrying" | "synced" | "syncing" | null

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function blankLevel(): LevelValues {
  return { attack: "2", health: "4", artwork: undefined, skills: [] }
}

function defaultValues(editing: ManagedHeroRecord | null): FormValues {
  if (!editing) {
    return {
      name: "",
      primaryColor: "red",
      secondaryColor: "",
      levels: [blankLevel(), blankLevel(), blankLevel()],
    }
  }
  const { hero } = editing
  return {
    name: hero.name,
    primaryColor: hero.primaryColor,
    secondaryColor: hero.secondaryColor ?? "",
    levels: hero.progression.map((lvl) => ({
      attack: String(lvl.card.stats.attack),
      health: String(lvl.card.stats.health),
      artwork: lvl.card.artwork,
      skills: lvl.card.selectedSkills,
    })) as [LevelValues, LevelValues, LevelValues],
  }
}

function computeLevelCost(lvl: LevelValues): number {
  const attack = Math.max(0, parseInt(lvl.attack, 10) || 0)
  const health = Math.max(1, parseInt(lvl.health, 10) || 1)
  const statCost = computeStatCost(attack, health)
  const skillCosts = computeSkillCostBreakdown(lvl.skills, SKILL_MAP, statCost)
  return Math.ceil(
    statCost +
      skillCosts.skillCostDelta +
      skillCosts.nonExhaustSurcharge +
      skillCosts.selfDestructSurcharge,
  )
}

/** Level-up cost = derived cost difference + 1 (minimum 1). */
function computeUpgradeCost(prevCost: number, thisCost: number): number {
  return Math.max(1, thisCost - prevCost + 1)
}

function getUpgradeCosts(costs: readonly [number, number, number]) {
  const [level1Cost, level2Cost, level3Cost] = costs
  return [
    undefined,
    computeUpgradeCost(level1Cost, level2Cost),
    computeUpgradeCost(level2Cost, level3Cost),
  ] as const
}

function makeArtworkStateTuple<T>(value: T): [T, T, T] {
  return [value, value, value]
}

function getArtworkSyncStatusLabel(
  t: ReturnType<typeof useI18n>["t"],
  status: Exclude<ArtworkSyncStatus, null>,
) {
  switch (status) {
    case "localOnly":
      return t("ui.artwork.sync.localOnly")
    case "retrying":
      return t("ui.artwork.sync.retrying")
    case "synced":
      return t("ui.artwork.sync.synced")
    case "syncing":
      return t("ui.artwork.sync.syncing")
  }
}

function filterSkillsForColors(
  skills: SkillSelection[],
  primary: CardColor,
  secondary: CardColor | "",
): SkillSelection[] {
  return skills.filter((sel) => {
    const def = SKILL_MAP.get(sel.skillId)
    if (!def) return false
    if (def.isColorless) return true
    return def.affinity === primary || def.affinity === secondary
  })
}

function buildHeroDefinition(
  values: FormValues,
  existingId: string | undefined,
): HeroDefinition {
  const heroId = existingId ?? createHeroId(values.name.trim())
  const slug = values.name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, "_")
    .replace(/^_+|_+$/gu, "")

  const costs = values.levels.map(computeLevelCost) as [number, number, number]
  const upgradeCosts = getUpgradeCosts(costs)

  return {
    heroId,
    slug,
    name: values.name.trim(),
    primaryColor: values.primaryColor,
    secondaryColor: values.secondaryColor || undefined,
    progression: values.levels.map((lvl, i) => {
      const attack = parseInt(lvl.attack, 10) || 0
      const health = parseInt(lvl.health, 10) || 1
      const statCost = computeStatCost(attack, health)
      const skillCosts = computeSkillCostBreakdown(
        lvl.skills,
        SKILL_MAP,
        statCost,
      )
      const derivedCost = Math.ceil(
        statCost +
          skillCosts.skillCostDelta +
          skillCosts.nonExhaustSurcharge +
          skillCosts.selfDestructSurcharge,
      )
      const targetCost = upgradeCosts[i] ?? 0
      return {
        level: (i + 1) as 1 | 2 | 3,
        targetCost,
        card: {
          stats: { attack, health },
          artwork: lvl.artwork,
          selectedSkills: lvl.skills,
          authoredCostInputs: {
            skillCostDelta: skillCosts.skillCostDelta,
            nonExhaustSurcharge: skillCosts.nonExhaustSurcharge,
            selfDestructSurcharge: skillCosts.selfDestructSurcharge,
            secondaryColorSurcharge: 0,
          },
          derivedCost,
        },
      }
    }) as HeroDefinition["progression"],
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function HeroEditorModal({
  open,
  onClose,
  editingRecord,
  onSave,
}: Props) {
  const { t } = useI18n()
  const { userId } = useAuth()
  const artworkSyncMutation = useArtworkSyncMutation()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [dialogEl, setDialogEl] = useState<HTMLDialogElement | null>(null)
  const handleDialogRef = useCallback((el: HTMLDialogElement | null) => {
    dialogRef.current = el
    setDialogEl(el)
  }, [])
  const [values, setValues] = useState<FormValues>(() =>
    defaultValues(editingRecord),
  )
  const [errors, setErrors] = useState<string[]>([])
  const [artworkErrors, setArtworkErrors] = useState<
    [string | null, string | null, string | null]
  >(makeArtworkStateTuple<string | null>(null))
  const [artworkBusy, setArtworkBusy] = useState<[boolean, boolean, boolean]>(
    makeArtworkStateTuple(false),
  )
  const [artworkSyncBusy, setArtworkSyncBusy] = useState<
    [boolean, boolean, boolean]
  >(makeArtworkStateTuple(false))
  const [artworkSyncStatus, setArtworkSyncStatus] = useState<
    [ArtworkSyncStatus, ArtworkSyncStatus, ArtworkSyncStatus]
  >(makeArtworkStateTuple<ArtworkSyncStatus>(null))
  const [, setModerationIncidents] = useStoredState(
    MODERATION_INCIDENT_STORAGE_KEY,
    ModerationIncidentStoreSchema,
    {
      defaultValue: EMPTY_MODERATION_INCIDENT_STORE,
      version: MODERATION_INCIDENT_STORAGE_VERSION,
    },
  )
  const [, setArtworkUploads] = useStoredState(
    ARTWORK_UPLOAD_STORAGE_KEY,
    ArtworkUploadStoreSchema,
    {
      defaultValue: EMPTY_ARTWORK_UPLOAD_STORE,
      version: ARTWORK_UPLOAD_STORAGE_VERSION,
    },
  )
  const [artworkAssets, , setArtworkAssets] = useIdbState(
    ARTWORK_ASSET_STORAGE_KEY,
    ArtworkAssetStoreSchema,
    {
      defaultValue: EMPTY_ARTWORK_ASSET_STORE,
      version: ARTWORK_ASSET_STORAGE_VERSION,
    },
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on reopen
  useEffect(() => {
    setValues(defaultValues(editingRecord))
    setErrors([])
    setArtworkErrors(makeArtworkStateTuple<string | null>(null))
    setArtworkBusy(makeArtworkStateTuple(false))
    setArtworkSyncBusy(makeArtworkStateTuple(false))
    setArtworkSyncStatus(makeArtworkStateTuple<ArtworkSyncStatus>(null))
  }, [editingRecord, open])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    else if (!open && dialog.open) dialog.close()
  }, [open])

  const updateLevel = (idx: 0 | 1 | 2) => (next: LevelValues) => {
    setValues((prev) => {
      const levels = [...prev.levels] as [LevelValues, LevelValues, LevelValues]
      levels[idx] = next
      return { ...prev, levels }
    })
  }

  const handleRandomize = () => {
    const {
      levels: randomLevels,
      primaryColor,
      secondaryColor,
    } = randomizeHeroLevels()
    setValues((prev) => ({
      ...prev,
      primaryColor,
      secondaryColor: secondaryColor ?? "",
      levels: prev.levels.map((lvl, i) => ({
        ...lvl,
        attack: String(randomLevels[i].attack),
        health: String(randomLevels[i].health),
        skills: randomLevels[i].skills,
      })) as [LevelValues, LevelValues, LevelValues],
    }))
  }

  const syncArtworkEnvelope = async (artwork: CardArtwork) => {
    if (!userId) {
      throw new Error(t("ui.artwork.error.syncUnavailable"))
    }

    const assets = collectArtworkAssets(artwork, artworkAssets)
    if (assets.length === 0) {
      throw new Error(t("ui.artwork.error.noLocalVariants"))
    }

    const turnstileToken = await requestTurnstileToken()
    return artworkSyncMutation.mutateAsync({
      payload: { artwork, assets },
      turnstileToken,
    })
  }

  const handleArtworkFile = async (
    levelIndex: 0 | 1 | 2,
    file: File | null,
  ) => {
    if (!file) return

    const uploadRecord = createArtworkUploadRecord({
      originalFilename: file.name,
      mimeType: file.type || "application/octet-stream",
      byteLength: file.size,
    })

    setArtworkUploads((previous) => upsertArtworkUpload(previous, uploadRecord))
    setArtworkBusy((previous) => {
      const next = [...previous] as [boolean, boolean, boolean]
      next[levelIndex] = true
      return next
    })
    setArtworkErrors((previous) => {
      const next = [...previous] as [
        string | null,
        string | null,
        string | null,
      ]
      next[levelIndex] = null
      return next
    })
    setArtworkSyncStatus((previous) => {
      const next = [...previous] as [
        ArtworkSyncStatus,
        ArtworkSyncStatus,
        ArtworkSyncStatus,
      ]
      next[levelIndex] = null
      return next
    })

    try {
      const processed = await processArtworkFile(file)
      setArtworkAssets((previous) =>
        upsertArtworkAssets(previous, processed.assets),
      )
      let syncedArtwork = processed.artwork
      try {
        setArtworkSyncBusy((previous) => {
          const next = [...previous] as [boolean, boolean, boolean]
          next[levelIndex] = true
          return next
        })
        setArtworkSyncStatus((previous) => {
          const next = [...previous] as [
            ArtworkSyncStatus,
            ArtworkSyncStatus,
            ArtworkSyncStatus,
          ]
          next[levelIndex] = "syncing"
          return next
        })
        if (!userId) {
          throw new Error(t("ui.artwork.error.syncUnavailable"))
        }
        const turnstileToken = await requestTurnstileToken()
        const synced = await artworkSyncMutation.mutateAsync({
          payload: processed,
          turnstileToken,
        })
        syncedArtwork = synced.artwork
        setArtworkSyncStatus((previous) => {
          const next = [...previous] as [
            ArtworkSyncStatus,
            ArtworkSyncStatus,
            ArtworkSyncStatus,
          ]
          next[levelIndex] = "synced"
          return next
        })
        setArtworkErrors((previous) => {
          const next = [...previous] as [
            string | null,
            string | null,
            string | null,
          ]
          next[levelIndex] = null
          return next
        })
      } catch (error) {
        // Keep the local cache when the worker media endpoint is not available.
        const message =
          getArtworkErrorLabel(t, error) ?? t("ui.artwork.error.uploadFailed")
        setArtworkErrors((previous) => {
          const next = [...previous] as [
            string | null,
            string | null,
            string | null,
          ]
          next[levelIndex] = message
          return next
        })
        setArtworkSyncStatus((previous) => {
          const next = [...previous] as [
            ArtworkSyncStatus,
            ArtworkSyncStatus,
            ArtworkSyncStatus,
          ]
          next[levelIndex] = "localOnly"
          return next
        })
      } finally {
        setArtworkSyncBusy((previous) => {
          const next = [...previous] as [boolean, boolean, boolean]
          next[levelIndex] = false
          return next
        })
      }
      setArtworkUploads((previous) =>
        updateArtworkUploadStatus(previous, uploadRecord.uploadId, "ready", {
          hash: syncedArtwork.hash,
          variants: syncedArtwork.variants,
        }),
      )
      setValues((previous) => {
        const levels = [...previous.levels] as [
          LevelValues,
          LevelValues,
          LevelValues,
        ]
        levels[levelIndex] = {
          ...levels[levelIndex],
          artwork: syncedArtwork,
        }
        return { ...previous, levels }
      })
    } catch (error) {
      const message =
        getArtworkErrorLabel(t, error) ?? t("ui.artwork.error.uploadFailed")
      setArtworkErrors((previous) => {
        const next = [...previous] as [
          string | null,
          string | null,
          string | null,
        ]
        next[levelIndex] = message
        return next
      })
      setArtworkUploads((previous) =>
        updateArtworkUploadStatus(previous, uploadRecord.uploadId, "rejected", {
          rejectionReason: message,
        }),
      )
    } finally {
      setArtworkBusy((previous) => {
        const next = [...previous] as [boolean, boolean, boolean]
        next[levelIndex] = false
        return next
      })
    }
  }

  const handleRetryArtworkSync = async (levelIndex: 0 | 1 | 2) => {
    const artwork = values.levels[levelIndex].artwork
    if (!artwork) return

    setArtworkSyncBusy((previous) => {
      const next = [...previous] as [boolean, boolean, boolean]
      next[levelIndex] = true
      return next
    })
    setArtworkSyncStatus((previous) => {
      const next = [...previous] as [
        ArtworkSyncStatus,
        ArtworkSyncStatus,
        ArtworkSyncStatus,
      ]
      next[levelIndex] = "retrying"
      return next
    })
    setArtworkErrors((previous) => {
      const next = [...previous] as [
        string | null,
        string | null,
        string | null,
      ]
      next[levelIndex] = null
      return next
    })

    try {
      const synced = await syncArtworkEnvelope(artwork)
      updateLevel(levelIndex)({
        ...values.levels[levelIndex],
        artwork: synced.artwork,
      })
      setArtworkSyncStatus((previous) => {
        const next = [...previous] as [
          ArtworkSyncStatus,
          ArtworkSyncStatus,
          ArtworkSyncStatus,
        ]
        next[levelIndex] = "synced"
        return next
      })
    } catch (error) {
      const message =
        getArtworkErrorLabel(t, error) ?? t("ui.artwork.error.uploadFailed")
      setArtworkErrors((previous) => {
        const next = [...previous] as [
          string | null,
          string | null,
          string | null,
        ]
        next[levelIndex] = message
        return next
      })
      setArtworkSyncStatus((previous) => {
        const next = [...previous] as [
          ArtworkSyncStatus,
          ArtworkSyncStatus,
          ArtworkSyncStatus,
        ]
        next[levelIndex] = "localOnly"
        return next
      })
    } finally {
      setArtworkSyncBusy((previous) => {
        const next = [...previous] as [boolean, boolean, boolean]
        next[levelIndex] = false
        return next
      })
    }
  }

  const handleSave = () => {
    const errs: string[] = []
    if (!values.name.trim()) errs.push(t("heroEditor.error.nameRequired"))
    const moderatedNameError = validateModeratedText(
      values.name,
      t("heroEditor.moderationLabel"),
      (label) => t("moderation.error.blockedLanguage", { label }),
    )
    if (moderatedNameError) {
      errs.push(moderatedNameError)
      setModerationIncidents((previous) => [
        createModerationIncident("hero_name", values.name),
        ...previous,
      ])
    }
    values.levels.forEach((lvl, i) => {
      const atk = parseInt(lvl.attack, 10)
      const hp = parseInt(lvl.health, 10)
      if (Number.isNaN(atk) || atk < 0)
        errs.push(t("heroEditor.error.attack", { level: i + 1 }))
      if (Number.isNaN(hp) || hp < 1)
        errs.push(t("heroEditor.error.health", { level: i + 1 }))
      const ids = lvl.skills.map((s) => s.skillId)
      if (new Set(ids).size !== ids.length)
        errs.push(t("heroEditor.error.duplicateSkill", { level: i + 1 }))
    })
    if (errs.length > 0) {
      setErrors(errs)
      return
    }
    const hero = buildHeroDefinition(values, editingRecord?.hero.heroId)
    const validationErrors = validateHero(hero)
      .issues.filter((issue) => issue.severity === "error")
      .map((issue) => getHeroValidationIssueLabel(t, issue))
    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return
    }
    const record: ManagedHeroRecord = {
      hero,
      ownerUserId: userId ?? editingRecord?.ownerUserId,
      remoteSyncFailedAt: undefined,
      remoteSyncedAt: undefined,
      updatedAt: new Date().toISOString(),
    }
    setErrors([])
    onSave(markRecordSyncPending(record))
  }

  const costs = values.levels.map(computeLevelCost) as [number, number, number]
  const upgradeCosts = getUpgradeCosts(costs)

  const getLevelAttack = (i: 0 | 1 | 2) =>
    Math.max(0, parseInt(values.levels[i].attack, 10) || 0)
  const getLevelHealth = (i: 0 | 1 | 2) =>
    Math.max(1, parseInt(values.levels[i].health, 10) || 1)

  const makeLevelEditing = (i: 0 | 1 | 2): CardEditingCallbacks => ({
    onNameChange: (name) => setValues((prev) => ({ ...prev, name })),
    onPrimaryColorChange: (primaryColor) => {
      setValues((prev) => ({
        ...prev,
        primaryColor,
        levels: prev.levels.map((lvl) => ({
          ...lvl,
          skills: filterSkillsForColors(
            lvl.skills,
            primaryColor,
            prev.secondaryColor,
          ),
        })) as [LevelValues, LevelValues, LevelValues],
      }))
    },
    onSecondaryColorChange: (color) => {
      setValues((prev) => ({
        ...prev,
        secondaryColor: color ?? "",
        levels: prev.levels.map((lvl) => ({
          ...lvl,
          skills: filterSkillsForColors(
            lvl.skills,
            prev.primaryColor,
            color ?? "",
          ),
        })) as [LevelValues, LevelValues, LevelValues],
      }))
    },
    onAttackChange: (attack) =>
      updateLevel(i)({ ...values.levels[i], attack: String(attack) }),
    onHealthChange: (health) =>
      updateLevel(i)({ ...values.levels[i], health: String(health) }),
    onSkillsChange: (skills) => {
      updateLevel(i)({ ...values.levels[i], skills })
    },
    forbiddenSkillIds: HERO_FORBIDDEN_SKILL_IDS,
  })

  return (
    <dialog
      className="card-editor-dialog hero-editor-dialog-new"
      onCancel={(e) => {
        e.preventDefault()
        onClose()
      }}
      ref={handleDialogRef}
    >
      <PortalTargetProvider value={dialogEl}>
        <div className="card-editor-inner">
          <div className="card-editor-header">
            <h2>
              {editingRecord
                ? t("heroEditor.editTitle")
                : t("heroEditor.createTitle")}
            </h2>
            <button
              aria-label={t("heroEditor.closeAria")}
              className="card-editor-close"
              onClick={onClose}
              type="button"
            >
              ✕
            </button>
          </div>

          <form
            className="card-editor-form"
            onSubmit={(e) => {
              e.preventDefault()
              handleSave()
            }}
          >
            <div className="hero-cards-row">
              {([0, 1, 2] as const).map((i) => (
                <div key={i} className="hero-card-preview-col">
                  <CardFace
                    artwork={values.levels[i].artwork}
                    artworkAssets={artworkAssets}
                    name={values.name}
                    primaryColor={values.primaryColor}
                    secondaryColor={values.secondaryColor || undefined}
                    stats={{
                      attack: getLevelAttack(i),
                      health: getLevelHealth(i),
                    }}
                    cost={costs[i]}
                    skills={values.levels[i].skills}
                    isHero
                    heroLevel={(i + 1) as 1 | 2 | 3}
                    size="md"
                    editing={makeLevelEditing(i)}
                    artworkEditorOverlay={
                      <>
                        <ArtworkDropTarget
                          addLabel={t("heroEditor.addImageLevel", {
                            level: i + 1,
                          })}
                          artwork={values.levels[i].artwork}
                          busy={artworkBusy[i]}
                          onSelectFile={(file) =>
                            void handleArtworkFile(i, file)
                          }
                          replaceLabel={t("heroEditor.replaceImageLevel", {
                            level: i + 1,
                          })}
                        />
                        <ArtworkOverlayControls
                          error={artworkErrors[i]}
                          onRemove={
                            values.levels[i].artwork
                              ? () => {
                                  updateLevel(i)({
                                    ...values.levels[i],
                                    artwork: undefined,
                                  })
                                  setArtworkErrors((previous) => {
                                    const next = [...previous] as [
                                      string | null,
                                      string | null,
                                      string | null,
                                    ]
                                    next[i] = null
                                    return next
                                  })
                                }
                              : undefined
                          }
                          onRetrySync={
                            values.levels[i].artwork &&
                            artworkSyncStatus[i] === "localOnly"
                              ? () => void handleRetryArtworkSync(i)
                              : undefined
                          }
                          removeLabel={t("ui.artwork.removeImage")}
                          retrySyncBusy={artworkSyncBusy[i]}
                          retrySyncLabel={t("ui.artwork.retrySync")}
                          retryingLabel={t("ui.artwork.retrying")}
                          syncStatusLabel={
                            artworkSyncStatus[i] &&
                            artworkSyncStatus[i] !== "synced"
                              ? getArtworkSyncStatusLabel(
                                  t,
                                  artworkSyncStatus[i],
                                )
                              : null
                          }
                        />
                      </>
                    }
                    style={{ width: "100%" }}
                  />
                  {i > 0 && (
                    <p className="hero-upgrade-label">
                      <CostBadge
                        cost={upgradeCosts[i] ?? 0}
                        size="m"
                        variant="subtle"
                      />{" "}
                      {t("heroEditor.upgradeLabel")}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {errors.length > 0 && (
              <ul className="form-errors">
                {errors.map((e) => (
                  <li className="field-error" key={e}>
                    {e}
                  </li>
                ))}
              </ul>
            )}

            <div className="card-editor-actions">
              <button
                className="card-editor-action-button card-editor-action-button-secondary"
                onClick={handleRandomize}
                title={t("heroEditor.randomize")}
                type="button"
              >
                <Dices size={14} strokeWidth={2} />
                {t("heroEditor.randomize")}
              </button>
              <button
                className="card-editor-action-button card-editor-action-button-secondary"
                onClick={onClose}
                type="button"
              >
                {t("common.cancel")}
              </button>
              <button
                className="card-editor-action-button card-editor-action-button-primary"
                type="submit"
              >
                {editingRecord
                  ? t("common.saveChanges")
                  : t("table.heroes.create")}
              </button>
            </div>
          </form>
        </div>
      </PortalTargetProvider>
    </dialog>
  )
}
